import { FC, forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { MasterContext, UIContext } from "../../../../../shared/contexts";
import { PolicyOverdueDebt, PolicyOverdueDebtDetail, PolicyOverdueDebtTotalsByBadge } from "../../../../../shared/interfaces/services/policy-overdue-debt-service";
import PolicyOverdueDebtService from "../../../../../shared/services/collection/PolicyOverdueDebt";
import { CardSkeletons, ColSize, ErrorResults, RowSize } from "../../../../../shared/components";
import { ItemNotFound } from "../../../../../shared/components/ItemNotFound";
import { Grid } from "@hdi-seguros/web-components.grid";
import { InsurancePolicyNotFound } from "../../../../../assets";
import { mapOfPoliciesOverdueDebt } from "../../utils/map-of-policy-overdue-debt";
import { createXLSXFile, downloadBase64Files } from "../../../../../shared/utils";
import { PolicyOverdueDebtType } from "../../interfaces/PolicyOverdueDebtType";
import PolicyOverdueDebtTotalsCard from "../PolicyOverdueDebtTotalsCard";
import './index.css'
import PolicyOverdueDebtCard from "../PolicyOverdueDebtCard";
import { Card } from "antd";
import DocumentsService from "../../../../../shared/services/documents";
import { PolicyOverdueDebtPDFRequest } from "../../../../../shared/interfaces/services/policy-overdue-debt-pdf-request";
import dayjs from "dayjs";
import userHelper from "../../../../../shared/utils/userHelper";
import { Base64, FILE_TYPES } from "../../../../../shared/interfaces";
import { CompanyData } from "../../../../../shared/interfaces/services/company-data-enum";

interface PolicyOverdueDebtTabProps {
    type: PolicyOverdueDebtType;
}

export const PolicyOverdueDebtTab = forwardRef(({ type }: PolicyOverdueDebtTabProps, ref) => {
    const { isLoading, showError, setIsLoading, setShowError } = useContext( UIContext );
    const gridRef = useRef();

    const [ policyOverdueDebtDetailsList, setPolicyOverdueDebtDetailsList ] = useState<PolicyOverdueDebt>();
    const [policyOverdueDebtDetails, setPolicyOverdueDebtDetails] = useState<Array<PolicyOverdueDebtDetail>>([]);
    const [policyOverdueDebtTotals, setPolicyOverdueDebtTotals] = useState<Array<PolicyOverdueDebtTotalsByBadge>>([]);
    const { branchList, getBranchsList } = useContext(MasterContext);
    const userInfo = userHelper.getUser();


    const getPolicyOverdueDebtList = async () => {        
        setIsLoading(true);
        if(branchList?.length === 0) return;
        
        try {
            const response = await PolicyOverdueDebtService.getPolicyOverdueDebt(type);

            if (response) {
                const renamedPolicies = response.polizasConDeudaVencida?.detalles.map((policy) => ({
                    ...policy,
                    rama: getBranchDescription(policy.rama),
                }));  

                if (response.polizasConDeudaVencida) {
                    response.polizasConDeudaVencida.detalles = renamedPolicies!;
                }        
                
                setPolicyOverdueDebtDetailsList(response.polizasConDeudaVencida!);   
                setPolicyOverdueDebtTotals(response.polizasConDeudaVencida!.totalesPorMoneda)    
            }            
            
        } catch (error) {
            setShowError(true);
        } finally {
            setTimeout(() => {
                setIsLoading(false);
            }, 0);
        }
    }

    useEffect(() => {
        if (policyOverdueDebtDetailsList) {
            (gridRef.current as any).setPage(1);
        }
    }, [policyOverdueDebtDetailsList, setPolicyOverdueDebtDetailsList]);
    

    useEffect(() => {
        getPolicyOverdueDebtList();
    }, []);

    useImperativeHandle(ref, () => ({
        downloadExcel,
        downloadPDF
    }));

    const downloadExcel = () => {
        if (policyOverdueDebtDetails?.length) {
            createXLSXFile(mapOfPoliciesOverdueDebt(policyOverdueDebtDetailsList!.detalles, policyOverdueDebtDetailsList!.cabecera), 'Listado de Pólizas con Deudas');
        }
    };

    const downloadPDF = async() => {
        if (!policyOverdueDebtDetails?.length || !policyOverdueDebtDetailsList) return;

        const toNumber = (value: unknown) => {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : 0;
        };

        const { cabecera, detalles = [], totalesPorMoneda = [] } = policyOverdueDebtDetailsList;
        const actualizadoAl = `${cabecera.fechaUltimoProceso} ${cabecera.horaUltimoProceso}`;

        const mapDetail = (detail: any) => ({
            ...detail,
            poliza: toNumber(detail.poliza),
            suplemento: toNumber(detail.suplemento),
            masDeNoventa: detail.deudaMasDe90Dias,
            hastaNoventa: detail.deudaHasta90Dias,
            hastaSesenta: detail.deudaHasta60Dias,
            hastaTreinta: detail.deudaHasta30Dias,
            aVencer: detail.deudaPorVencer,
        });

        const mapTotal = (total: any) => ({
            ...total,
            masDeNoventa: total.deudaMasDe90Dias,
            hastaNoventa: total.deudaHasta90Dias,
            hastaSesenta: total.deudaHasta60Dias,
            hastaTreinta: total.deudaHasta30Dias,
            aVencer: total.deudaPorVencer,
        });

        const request:PolicyOverdueDebtPDFRequest = {
            pcia: CompanyData.COMPANY_NAME,
            actualizadoAl,
            fecha: actualizadoAl,
            nombreProductor: userInfo?.intermediarioPrincipal?.nombreInter!,
            codigoProductor: userInfo?.intermediarioPrincipal!.codigoInter!,
            fechaProceso: cabecera.fechaUltimoProceso!,
            horaProceso: cabecera.horaUltimoProceso!,
            primeraColumna: cabecera.firstColumn!,
            segundaColumna: cabecera.secondColumn!,
            terceraColumna: cabecera.thirdColumn!,
            polizasConDeudaVencida: detalles.map(mapDetail),
            totalDeudaVencida: totalesPorMoneda.map(mapTotal),
        }
        
        const response = await DocumentsService.getPolicyOverdueDebtPdf(request);

        downloadBase64Files(response.data.base64, `DeudaVencida_${dayjs().get("millisecond")}`, FILE_TYPES.PDF)
    };

    const getBranchDescription = useMemo(() => (branch: string) => {                
            return branchList?.find((b) => b.value === branch)?.label || branch;
    }, [branchList]);

    useEffect(() => {
        if (branchList && branchList.length > 0) {
            getPolicyOverdueDebtList();
        }
    }, [branchList]);

    useEffect(() => {
        setIsLoading(true)
        if(!branchList?.length) getBranchsList();
    }, [branchList])

    return (
        <div className="policy-overdue-debt">
            {
                showError 
                    ? <ErrorResults title="No se pudo cargar el listado de pólizas con deuda" />
                    : (
                        <RowSize rowProps={{ gutter: [15, 15], className: 'policy-overdue-debt_tabs' }}> 
                            <ColSize size={100}>
                                <Grid
                                    list={(policyOverdueDebtDetailsList?.detalles) || []} 
                                    setState={setPolicyOverdueDebtDetails} 
                                    name="polizas" 
                                    ref={gridRef}
                                >
                                    <ColSize className="policy-overdue-debt_cards" size={100}>
                                        {
                                            policyOverdueDebtDetails.length
                                                ? policyOverdueDebtDetails.map(( policy, index ) => (
                                                    <PolicyOverdueDebtCard
                                                        key={`policies-debt-${policy.poliza}-${index}`}
                                                        policy={policy}
                                                        loading={isLoading}
                                                        headers={policyOverdueDebtDetailsList!.cabecera}
                                                    />
                                                    ))
                                            : isLoading
                                            ? (
                                                <RowSize rowProps={{ gutter: [15, 15] }}>
                                                    <CardSkeletons />
                                                </RowSize>
                                            ) :  <ItemNotFound textDescription="No se encontraron pólizas con deuda" imageUrl={InsurancePolicyNotFound} />
                                        }
                                    </ColSize>
                                </Grid>
                            </ColSize>

                            <ColSize className="policy-overdue-debt_second-table" size={100}>
                                <ColSize className="policy-overdue-debt_cards" size={100}>
                                    {
                                        policyOverdueDebtTotals.length
                                            ? policyOverdueDebtTotals.map(( total, index ) => (
                                                <PolicyOverdueDebtTotalsCard
                                                    key={`policies-debt-${total.moneda}-${index}`}
                                                    total={total}
                                                    loading={isLoading}
                                                    headers={policyOverdueDebtDetailsList!.cabecera}
                                                />
                                                ))
                                        : isLoading
                                        ? (
                                            <RowSize rowProps={{ gutter: [15, 15] }}>
                                                <CardSkeletons />
                                            </RowSize>
                                        ) :  <Card>No se encontraron totales</Card>
                                    }
                                </ColSize>
                            </ColSize>
                        </RowSize>
                    )
            }
        </div>
    )
}
)